import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useDateFormat } from '@/lib/dates';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Download,
  Send,
  MoreHorizontal,
  Mail,
  Trash2,
  RefreshCw,
  Pencil,
  ArrowRightLeft,
  FileText,
  Merge,
  Clock,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { UnifiedInvoiceAPI } from '@/services/invoicing';
import { PeppolAPI } from '@/services/peppol';
import { Globe } from 'lucide-react';
import { StatusDropdown } from '@/components/common/StatusDropdown';
import { useAuth } from '@/hooks/useAuth';
import { firmHasPartialPayments } from '@/config/firm-features';
import { UnifiedInvoice } from '@/types/invoicing';
import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';
import { ColumnFilter } from './ColumnFilter';
import { DateColumnFilter } from './DateColumnFilter';
import { TablePagination } from '@/components/ui/table-pagination';
import { TableSkeletonRows, TableEmptyRow } from '@/components/ui/table-states';
import { formatCurrency } from '@/utils/document';

interface InvoiceTableProps {
  invoices: UnifiedInvoice[];
  isLoading: boolean;
  clientMap: Record<number, string>;
  matterMap: Record<number, string>;
  totals: {
    total_sum: number;
    subtotal_sum?: number;
    paid_sum?: number;
    vat_amount_sum: number;
    service_charge_sum: number;
    costs: number;
    count: number;
  };
  selectedInvoices: Set<number>;
  onInvoiceSelection: (invoiceId: number, isSelected: boolean) => void;
  onSelectAll: (isSelected: boolean) => void;
  onSendEmail: (invoice: UnifiedInvoice) => void;
  onEditInvoice: (invoice: UnifiedInvoice) => void;
  onApplyToInvoice: (invoice: UnifiedInvoice) => void;
  onStatusChanged: () => void;
  showBulkActions: boolean;
  onBulkEmailClick: () => void;
  onMergeClick?: () => void;
  currentPage: number;
  itemsPerPage: number;
  totalInvoices: number;
  onPageChange: (page: number) => void;
  onFiltersApplied: (filters: {
    types: Set<string>;
    clients: Set<number>;
    matters: Set<number>;
    statuses: Set<string>;
  }) => void;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  onDateFilterApply: (dateFrom: Date | undefined, dateTo: Date | undefined) => void;
}

export function InvoiceTable({
  invoices,
  isLoading,
  clientMap,
  matterMap,
  totals,
  selectedInvoices,
  onInvoiceSelection,
  onSelectAll,
  onSendEmail,
  onEditInvoice,
  onApplyToInvoice,
  onStatusChanged,
  showBulkActions,
  onBulkEmailClick,
  onMergeClick,
  currentPage,
  itemsPerPage,
  totalInvoices,
  onPageChange,
  onFiltersApplied,
  dateFrom,
  dateTo,
  onDateFilterApply,
}: InvoiceTableProps) {
  const { user } = useAuth();
  const showPartialPayments = firmHasPartialPayments(user?.firms);

  // Column filter states
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [selectedClients, setSelectedClients] = useState<Set<number>>(new Set());
  const [selectedMatters, setSelectedMatters] = useState<Set<number>>(new Set());
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set());

  // Format currency helper
  // Format date as MM/YY
  const formatDateShort = (dateString: string) => {
    const date = parseISO(dateString);
    return format(date, 'MM/yy');
  };

  // Format date using the user's preferred token (falls back to dd/mm/yyyy).
  const { format: formatByPref } = useDateFormat();
  const formatDateFull = (dateString: string) => {
    if (!dateString) return '';
    try {
      return formatByPref(parseISO(dateString));
    } catch {
      return format(parseISO(dateString), 'dd/MM/yyyy');
    }
  };

  // Generate PDF
  const handleGeneratePDF = async (invoice: UnifiedInvoice) => {
    try {
      const { blob, filename } = await UnifiedInvoiceAPI.generatePDF(invoice.id);

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      // Use filename from Content-Disposition header, fallback to invoice_number
      link.setAttribute('download', filename || `${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: 'Success',
        description: 'PDF downloaded successfully',
      });
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate PDF',
        variant: 'destructive',
      });
    }
  };

  // Download timesheets PDF
  const handleDownloadTimesheets = async (invoice: UnifiedInvoice) => {
    try {
      const { blob, filename } = await UnifiedInvoiceAPI.downloadTimesheets(invoice.id);

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename || `timesheets_invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast({ title: 'Success', description: 'Timesheets downloaded successfully' });
    } catch (error) {
      console.error('Failed to download timesheets:', error);
      toast({
        title: 'Error',
        description: 'Failed to download timesheets',
        variant: 'destructive',
      });
    }
  };

  // Download LEDES 1998B
  const handleDownloadLedes = async (invoice: UnifiedInvoice) => {
    try {
      const response = await api.get(
        `${ROUTES.API.INVOICING_UNIFIED}${invoice.id}/ledes-format/`,
        {
          params: { format_type: 'ledes_1998b' },
          responseType: 'blob',
        }
      );

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;

      const contentDisposition = response.headers['content-disposition'];
      let fileName = `${invoice.invoice_number}_ledes.txt`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) fileName = match[1];
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: 'Success',
        description: 'LEDES 1998B file downloaded successfully',
      });
    } catch (error) {
      console.error('Failed to download LEDES:', error);
      toast({
        title: 'Error',
        description: 'Failed to download LEDES 1998B format',
        variant: 'destructive',
      });
    }
  };

  // Download Word Document
  const handleDownloadWord = async (invoice: UnifiedInvoice) => {
    try {
      if (!invoice.document_file) {
        toast({
          title: 'Error',
          description: 'Word document not available',
          variant: 'destructive',
        });
        return;
      }

      // Open the document_file URL directly
      const link = document.createElement('a');
      link.href = invoice.document_file;
      link.setAttribute('download', `${invoice.invoice_number}.docx`);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: 'Success',
        description: 'Word document download started',
      });
    } catch (error) {
      console.error('Failed to download Word document:', error);
      toast({
        title: 'Error',
        description: 'Failed to download Word document',
        variant: 'destructive',
      });
    }
  };

  // Delete invoice
  const handleDelete = async (invoice: UnifiedInvoice) => {
    if (!confirm(`Are you sure you want to delete ${invoice.invoice_number}?`)) return;

    try {
      await UnifiedInvoiceAPI.deleteInvoice(invoice.id);

      toast({
        title: 'Success',
        description: `${invoice.invoice_number} deleted successfully`,
      });

      onStatusChanged();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete',
        variant: 'destructive',
      });
    }
  };

  // Regenerate document
  const handleRegenerateDocument = async (invoice: UnifiedInvoice) => {
    try {
      await UnifiedInvoiceAPI.regenerateDocument(invoice.id);
      
      toast({
        title: 'Success',
        description: 'Document regenerated successfully',
      });

      onStatusChanged();
    } catch (error) {
      console.error('Failed to regenerate document:', error);
      toast({
        title: 'Error',
        description: 'Failed to regenerate document',
        variant: 'destructive',
      });
    }
  };

  // Get type badge
  const getTypeBadge = (invoice: UnifiedInvoice) => {
    const typeConfig = {
      invoice: { label: 'INV', variant: 'default' as const },
      credit_note: { label: 'CN', variant: 'destructive' as const },
      downpayment: { label: 'DP', variant: 'secondary' as const },
    };

    const config = typeConfig[invoice.invoice_type];
    
    return (
      <Badge variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  // Costs billed on the invoice — the frozen costs_share, same source the PDF uses
  const calculateCosts = (invoice: UnifiedInvoice) => {
    return Number(invoice.costs_share) || 0;
  };

  // Legal fees = subtotal minus billed costs, matching the PDF's "Legal Services" line
  const calculateLegalFees = (invoice: UnifiedInvoice) => {
    return Math.max(0, (Number(invoice.subtotal) || 0) - calculateCosts(invoice));
  };

  // Calculate service charge for an invoice
  // The backend already stores service_charge as the per-participant amount
  const calculateServiceCharge = (invoice: UnifiedInvoice) => {
    return Number(invoice.service_charge) || 0;
  };

  // Calculate total legal fees from totals
  const calculateTotalLegalFees = () => {
    const subtotal = totals.subtotal_sum
      ?? (totals.total_sum - totals.vat_amount_sum - totals.service_charge_sum);
    return Math.max(0, subtotal - totals.costs);
  };

  // Check if any invoices have service charges
  const hasServiceCharges = useMemo(() => {
    return invoices.some(invoice => calculateServiceCharge(invoice) > 0) || totals.service_charge_sum > 0;
  }, [invoices, totals.service_charge_sum]);

  // Calculate totals for selected invoices
  const selectedTotals = useMemo(() => {
    if (selectedInvoices.size === 0) {
      return null;
    }

    const selectedInvoiceData = invoices.filter(inv => selectedInvoices.has(inv.id));
    
    let legalFeesSum = 0;
    let serviceChargeSum = 0;
    let costsSum = 0;
    let vatSum = 0;
    let totalSum = 0;

    selectedInvoiceData.forEach(invoice => {
      legalFeesSum += calculateLegalFees(invoice);
      serviceChargeSum += calculateServiceCharge(invoice);
      costsSum += calculateCosts(invoice);
      vatSum += Number(invoice.vat_amount) || 0;
      totalSum += Number(invoice.display_amount) || 0;
    });

    return {
      count: selectedInvoices.size,
      legalFees: legalFeesSum,
      serviceCharge: serviceChargeSum,
      costs: costsSum,
      vat: vatSum,
      total: totalSum,
    };
  }, [selectedInvoices, invoices]);

  // Hardcoded filter options for types (all possible types)
  const typeOptions = useMemo(() => [
    { label: 'Invoice', value: 'invoice' },
    { label: 'Credit Note', value: 'credit_note' },
    { label: 'Down Payment', value: 'downpayment' },
  ], []);

  // Hardcoded filter options for statuses (all possible statuses)
  const statusOptions = useMemo(() => [
    { label: 'Draft', value: 'draft' },
    { label: 'Sent', value: 'sent' },
    { label: 'Paid', value: 'paid' },
    ...(showPartialPayments ? [{ label: 'Partly Paid', value: 'partly_paid' }] : []),
    { label: 'Overdue', value: 'overdue' },
    { label: 'Cancelled', value: 'cancelled' },
    { label: 'Issued', value: 'issued' },
    { label: 'Applied', value: 'applied' },
    { label: 'Partially Applied', value: 'partially_applied' },
    { label: 'Fully Applied', value: 'fully_applied' },
  ], [showPartialPayments]);

  // Client options from clientMap (all clients)
  const clientOptions = useMemo(() => {
    return Object.entries(clientMap).map(([id, name]) => ({
      label: name,
      value: Number(id),
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [clientMap]);

  // Matter options from matterMap (all matters)
  const matterOptions = useMemo(() => {
    return Object.entries(matterMap).map(([id, name]) => ({
      label: name,
      value: Number(id),
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [matterMap]);

  // Handle filter changes
  const handleTypeFilterApply = (values: Set<string | number>) => {
    const stringValues = new Set(Array.from(values).map(v => String(v)));
    setSelectedTypes(stringValues);
    onFiltersApplied({
      types: stringValues,
      clients: selectedClients,
      matters: selectedMatters,
      statuses: selectedStatuses,
    });
  };

  const handleClientFilterApply = (values: Set<string | number>) => {
    const numberValues = new Set(Array.from(values).map(v => Number(v)));
    setSelectedClients(numberValues);
    onFiltersApplied({
      types: selectedTypes,
      clients: numberValues,
      matters: selectedMatters,
      statuses: selectedStatuses,
    });
  };

  const handleMatterFilterApply = (values: Set<string | number>) => {
    const numberValues = new Set(Array.from(values).map(v => Number(v)));
    setSelectedMatters(numberValues);
    onFiltersApplied({
      types: selectedTypes,
      clients: selectedClients,
      matters: numberValues,
      statuses: selectedStatuses,
    });
  };

  const handleStatusFilterApply = (values: Set<string | number>) => {
    const stringValues = new Set(Array.from(values).map(v => String(v)));
    setSelectedStatuses(stringValues);
    onFiltersApplied({
      types: selectedTypes,
      clients: selectedClients,
      matters: selectedMatters,
      statuses: stringValues,
    });
  };

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="px-3 py-1.5 bg-muted/50 rounded-lg border border-dashed">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="selectAll"
                  checked={selectedInvoices.size === invoices.length && invoices.length > 0}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="selectAll" className="text-sm font-medium">
                  Select Page ({selectedInvoices.size} of {invoices.length} on this page)
                </Label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedInvoices.size > 0 && (
                <>
                  {onMergeClick && selectedInvoices.size >= 2 && (
                    <Button onClick={onMergeClick} size="sm" variant="outline">
                      <Merge className="h-4 w-4 mr-2" />
                      Merge Selected ({selectedInvoices.size})
                    </Button>
                  )}
                  <Button onClick={onBulkEmailClick} size="sm">
                    <Send className="h-4 w-4 mr-2" />
                    Send Selected ({selectedInvoices.size})
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border">
        <Table className="text-xs [&_th]:px-1.5 [&_td]:px-1.5">
          <TableHeader>
            <TableRow>
              {showBulkActions && <TableHead className="w-8"></TableHead>}
              <TableHead>
                <div className="flex items-center gap-3">
                  Date
                  <DateColumnFilter
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onApply={onDateFilterApply}
                  />
                </div>
              </TableHead>
              <TableHead>Number</TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Type
                  <ColumnFilter
                    title="Filter by Type"
                    options={typeOptions}
                    selectedValues={selectedTypes}
                    onApply={handleTypeFilterApply}
                    searchable={false}
                  />
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Client
                  <ColumnFilter
                    title="Filter by Client"
                    options={clientOptions}
                    selectedValues={selectedClients}
                    onApply={handleClientFilterApply}
                  />
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Matter
                  <ColumnFilter
                    title="Filter by Matter"
                    options={matterOptions}
                    selectedValues={selectedMatters}
                    onApply={handleMatterFilterApply}
                  />
                </div>
              </TableHead>
              <TableHead className="text-right">Legal Fees</TableHead>
              {hasServiceCharges && <TableHead className="text-right">Service Charge</TableHead>}
              <TableHead className="text-right">Costs</TableHead>
              <TableHead className="text-right">VAT Amount</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Status
                  <ColumnFilter
                    title="Filter by Status"
                    options={statusOptions}
                    selectedValues={selectedStatuses}
                    onApply={handleStatusFilterApply}
                    searchable={false}
                  />
                </div>
              </TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableSkeletonRows colSpan={showBulkActions ? (hasServiceCharges ? 13 : 12) : (hasServiceCharges ? 12 : 11)} />
            ) : invoices.length === 0 ? (
              <TableEmptyRow colSpan={showBulkActions ? (hasServiceCharges ? 13 : 12) : (hasServiceCharges ? 12 : 11)}>
                No invoices found
              </TableEmptyRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  {showBulkActions && (
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedInvoices.has(invoice.id)}
                        onChange={(e) => onInvoiceSelection(invoice.id, e.target.checked)}
                        className="rounded"
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            {formatDateShort(invoice.issue_date)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{formatDateFull(invoice.issue_date)}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell className="font-medium">
                    <span className="select-text">
                      {invoice.invoice_number}
                    </span>
                  </TableCell>
                  <TableCell>
                    {getTypeBadge(invoice)}
                  </TableCell>
                  <TableCell>
                    <span
                      className="line-clamp-2 max-w-36"
                      title={invoice.client_name || clientMap[invoice.client] || `Client #${invoice.client}`}
                    >
                      {invoice.client_name || clientMap[invoice.client] || `Client #${invoice.client}`}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className="line-clamp-2 max-w-40"
                      title={invoice.matter_name || matterMap[invoice.matter] || `Matter #${invoice.matter}`}
                    >
                      {invoice.matter_name || matterMap[invoice.matter] || `Matter #${invoice.matter}`}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(calculateLegalFees(invoice))}
                  </TableCell>
                  {hasServiceCharges && (
                    <TableCell className="text-right font-medium">
                      {formatCurrency(calculateServiceCharge(invoice))}
                    </TableCell>
                  )}
                  <TableCell className="text-right font-medium">
                    {formatCurrency(calculateCosts(invoice))}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(Number(invoice.vat_amount) || 0)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(invoice.display_amount)}
                  </TableCell>
                  <TableCell>
                    <StatusDropdown
                      documentType={
                        invoice.invoice_type === 'invoice' ? 'invoice' :
                        invoice.invoice_type === 'credit_note' ? 'credit_note' :
                        'down_payment'
                      }
                      documentId={invoice.id}
                      documentNumber={invoice.invoice_number}
                      currentStatus={invoice.status}
                      onStatusChanged={onStatusChanged}
                      totalAmount={Number(invoice.total) || 0}
                      amountPaid={Number(invoice.amount_paid) || 0}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Invoice actions">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onEditInvoice(invoice)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit Invoice
                          </DropdownMenuItem>
                          {invoice.invoice_type === 'invoice' && invoice.status !== 'cancelled' && (
                            <DropdownMenuItem onClick={() => onApplyToInvoice(invoice)}>
                              <ArrowRightLeft className="h-4 w-4 mr-2" />
                              Apply Credit/Down Payment
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleGeneratePDF(invoice)}>
                            <Download className="h-4 w-4 mr-2" />
                            Download PDF
                          </DropdownMenuItem>
                          {invoice.document_file && (
                            <DropdownMenuItem onClick={() => handleDownloadWord(invoice)}>
                              <Download className="h-4 w-4 mr-2" />
                              Download Word Document
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDownloadTimesheets(invoice)}>
                            <Clock className="h-4 w-4 mr-2" />
                            Download Timesheets
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadLedes(invoice)}>
                            <FileText className="h-4 w-4 mr-2" />
                            Download LEDES 1998B
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleRegenerateDocument(invoice)}>  
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Regenerate Document
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onSendEmail(invoice)}>
                            <Mail className="h-4 w-4 mr-2" />
                            Send Email
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={async () => {
                            try {
                              toast({ title: 'Sending via Peppol...', description: `Invoice ${invoice.invoice_number}` });
                              await PeppolAPI.sendInvoiceViaPeppol(invoice.id);
                              toast({ title: 'Sent via Peppol', description: `Invoice ${invoice.invoice_number} was sent successfully.` });
                              onStatusChanged();
                            } catch (error: any) {
                              const data = error?.response?.data || {};
                              const baseMsg = data.error || data.detail || 'Failed to send invoice via Peppol';
                              const errs: string[] = data.errors || data.validation_errors || [];
                              const description = errs.length > 0
                                ? `${baseMsg}: ${errs.slice(0, 3).join('; ')}${errs.length > 3 ? ` (+${errs.length - 3} more)` : ''}`
                                : baseMsg;
                              toast({ title: 'Peppol Send Failed', description, variant: 'destructive' });
                            }
                          }}>
                            <Globe className="h-4 w-4 mr-2" />
                            Send via Peppol
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDelete(invoice)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
            
            {/* Totals Row */}
            {!isLoading && invoices.length > 0 && (
              <TableRow className="bg-muted/50 font-semibold border-t-2">
                {showBulkActions && <TableCell></TableCell>}
                <TableCell className="font-bold">TOTALS</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell className="text-right">{formatCurrency(calculateTotalLegalFees())}</TableCell>
                {hasServiceCharges && (
                  <TableCell className="text-right">{formatCurrency(totals.service_charge_sum)}</TableCell>
                )}
                <TableCell className="text-right">{formatCurrency(totals.costs)}</TableCell>
                <TableCell className="text-right">{formatCurrency(totals.vat_amount_sum)}</TableCell>
                <TableCell className="text-right">{formatCurrency(totals.total_sum)}</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}

            {/* Paid vs invoiced (partial-payment firms) */}
            {!isLoading && invoices.length > 0 && showPartialPayments && (
              <TableRow className="bg-muted/30">
                {showBulkActions && <TableCell></TableCell>}
                <TableCell className="font-semibold">PAID</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                {hasServiceCharges && <TableCell></TableCell>}
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell className="text-right font-semibold">
                  {formatCurrency(totals.paid_sum ?? 0)}
                </TableCell>
                <TableCell colSpan={2} className="text-muted-foreground whitespace-nowrap">
                  of {formatCurrency(totals.total_sum)} invoiced
                </TableCell>
              </TableRow>
            )}

            {/* Selected Totals Row */}
            {!isLoading && selectedTotals && (
              <TableRow className="bg-primary/10 font-semibold border-t-2">
                {showBulkActions && <TableCell></TableCell>}
                <TableCell className="font-bold">SELECTED ({selectedTotals.count})</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell className="text-right">{formatCurrency(selectedTotals.legalFees)}</TableCell>
                {hasServiceCharges && (
                  <TableCell className="text-right">{formatCurrency(selectedTotals.serviceCharge)}</TableCell>
                )}
                <TableCell className="text-right">{formatCurrency(selectedTotals.costs)}</TableCell>
                <TableCell className="text-right">{formatCurrency(selectedTotals.vat)}</TableCell>
                <TableCell className="text-right">{formatCurrency(selectedTotals.total)}</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <TablePagination
        currentPage={currentPage}
        totalItems={totalInvoices}
        itemsPerPage={itemsPerPage}
        onPageChange={onPageChange}
        itemLabel="invoices"
      />
    </div>
  );
}
